import { useEffect, type ReactNode } from 'react';
import { Router } from 'wouter';
import { useTranslation } from 'react-i18next';
import {
  getSlugFromPath,
  slugToLocale,
  detectBrowserSlug,
  buildLocalePath,
  stripLocaleFromPath,
  DEFAULT_SLUG,
  type LocaleSlug,
} from '@/i18n/locale-utils';

interface LocaleRouterProps {
  children: ReactNode;
}

export function LocaleRouter({ children }: LocaleRouterProps) {
  const { i18n } = useTranslation();
  const path = window.location.pathname;
  const slug = getSlugFromPath(path);

  // If no valid locale slug in URL, redirect to detected locale
  if (!slug) {
    const detected = detectBrowserSlug();
    const subpath = stripLocaleFromPath(path);
    const target = buildLocalePath(detected, subpath);
    window.location.replace(target);
    return null;
  }

  return <LocaleRouterInner slug={slug}>{children}</LocaleRouterInner>;
}

function LocaleRouterInner({
  slug,
  children,
}: {
  slug: LocaleSlug;
  children: ReactNode;
}) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const locale = slugToLocale[slug];
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [slug, i18n]);

  return <Router base={`/${slug}`}>{children}</Router>;
}
